import {
  Typography,
  Select,
  MenuItem,
  FormControl,
  makeStyles,
  Input,
} from '@material-ui/core'
import React from 'react'
import Button from './Button'
import clsx from 'clsx'
import { ReGenerateIcon } from 'app/components/icons'
import { FormattedMessage, useIntl } from 'react-intl'

export const toneOptions = (formatMessage) => [
  { label: formatMessage({ id: 'abby.tone.formal' }), id: 'formal' },
  { label: formatMessage({ id: 'abby.tone.casual' }), id: 'casual' },
  { label: formatMessage({ id: 'abby.tone.neutral' }), id: 'neutral' },
  { label: formatMessage({ id: 'abby.tone.urgent' }), id: 'urgent' },
  { label: formatMessage({ id: 'abby.tone.promotional' }), id: 'promotional' },
  {
    label: formatMessage({ id: 'abby.tone.inspirational' }),
    id: 'inspirational',
  },
  { label: formatMessage({ id: 'abby.tone.humorous' }), id: 'humorous' },
]

const improveOptions = (formatMessage) => [
  { label: formatMessage({ id: 'abby.improve.shorten' }), id: 'shorten' },
  { label: formatMessage({ id: 'abby.improve.lengthen' }), id: 'lengthen' },
  {
    label: formatMessage({ id: 'abby.improve.enhance.clarity' }),
    id: 'enhance clarity',
  },
  {
    label: formatMessage({ id: 'abby.improve.make.more.engaging' }),
    id: 'make more engaging',
  },
  {
    label: formatMessage({ id: 'abby.improve.increase.specificity' }),
    id: 'increase specificity',
  },
  { label: formatMessage({ id: 'abby.improve.generalize' }), id: 'generalize' },
  {
    label: formatMessage({ id: 'abby.improve.optimize.for.seo' }),
    id: 'optimize for seo',
  },
]

const useStyles = makeStyles(() => ({
  formControl: {
    '& .MuiOutlinedInput-root': {
      border: 'none',
      boxShadow: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  select: {
    border: 'none',
    minWidth: '130px',
    width: 'auto',
    padding: '5px 10px',
  },
}))

export default function RegenerateContentSection({
  onRegenerateContentClick,
  tone,
  onToneChange,
  improveOption,
  onImproveOptionChange,
  tourClassName,
}) {
  const classes = useStyles()
  const { formatMessage } = useIntl()

  const formattedToneOptions = toneOptions(formatMessage)
  const formattedImproveOptions = improveOptions(formatMessage)

  return (
    <div
      className={clsx(
        'flex justify-between items-center border border-secondary rounded-8 py-4 px-5',
        tourClassName,
      )}
    >
      <div className="flex">
        <div className="flex items-center">
          <div className="px-5 font-medium">
            <FormattedMessage id="abby.action.change.tone" />
          </div>
          <FormControl
            className={clsx('m-6 border-0', classes.formControl)}
            variant="outlined"
          >
            <Select
              labelId="tone-label"
              id="tone-select"
              value={tone || ''}
              className={classes.select}
              disableUnderline={true}
              onChange={(e) => {
                onToneChange(e.target.value)
              }}
              displayEmpty
              renderValue={(selected) =>
                selected === '' ? (
                  <span className="opacity-50">
                    <FormattedMessage id="abby.select.none" />
                  </span>
                ) : (
                  formattedToneOptions.find((option) => option.id === selected)
                    ?.label
                )
              }
              input={
                <Input
                  id="tone-select"
                  className="p-14 h-48 rounded-8 border border-grey-input text-blue-950"
                />
              }
            >
              <MenuItem id="none" value="">
                <FormattedMessage id="abby.select.none" />
              </MenuItem>
              {formattedToneOptions.map((option) => (
                <MenuItem
                  id={option.id}
                  value={option.id}
                  key={`toneOptions-${option.id}`}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className="flex items-center">
          <div className="px-5 font-medium">
            <FormattedMessage id="abby.action.improve.message" />
          </div>
          <FormControl
            className={clsx('m-6 border-0', classes.formControl)}
            variant="outlined"
          >
            <Select
              labelId="improve-option-label"
              id="improve-option-select"
              value={improveOption || ''}
              className={classes.select}
              disableUnderline={true}
              onChange={(e) => {
                onImproveOptionChange(e.target.value)
              }}
              displayEmpty
              renderValue={(selected) =>
                selected === '' ? (
                  <span className="opacity-50">
                    <FormattedMessage id="abby.select.none" />
                  </span>
                ) : (
                  formattedImproveOptions.find(
                    (option) => option.id === selected,
                  )?.label
                )
              }
              input={
                <Input
                  id="tone-select"
                  className="p-14 h-48 rounded-8 border border-grey-input text-blue-950"
                />
              }
            >
              <MenuItem id="none" value="">
                <FormattedMessage id="abby.select.none" />
              </MenuItem>
              {formattedImproveOptions.map((option) => (
                <MenuItem
                  id={option.id}
                  value={option.id}
                  key={`improveOptions-${option.id}`}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>
      <div className="w-88 p-4">
        <Button
          onClick={() => {
            onRegenerateContentClick()
          }}
          className="mx-4 border-1 border-gray-300 space-x-8 px-14 h-44 bg-primary-200 text-secondary hover:bg-primary-350"
        >
          <ReGenerateIcon classes="w-24" />
        </Button>
      </div>
    </div>
  )
}
